import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ArrowRight } from "lucide-react";
import { Header } from "../components/landing/Header";
import { Seo } from "../components/Seo";
import { Footer } from "../components/landing/Footer";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  ORG_TYPES, CONTACT_METHODS, SERVICES, CLOUDS, COMPLIANCE, TIMELINES, INITIAL_FORM,
  inputCls, Section, Field, CheckGroup, RadioPills,
} from "../components/intake/FormControls";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IntakeForm() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(() => {
    const preselected = searchParams.getAll("services").filter((s) => SERVICES.includes(s));
    return preselected.length ? { ...INITIAL_FORM, services: preselected } : INITIAL_FORM;
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const toggle = (key) => (opt) =>
    setForm((f) => ({ ...f, [key]: f[key].includes(opt) ? f[key].filter((v) => v !== opt) : [...f[key], opt] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.org_name || !form.full_name || !form.email) {
      toast.error("Please fill in Organization Name, Full Name, and Email Address.");
      return;
    }
    if (!form.authorized) {
      toast.error("Please check the authorization box to submit.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setSubmitted(true);
      toast.success("Intake form submitted. Our advisory team will be in touch.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed. Please try again or email janthony@silentsentrycorp.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#05071F] text-white min-h-screen font-body">
      <Seo
        title="Request a Free Assessment — Customer Intake | Silent Sentry, LLC"
        description="Complete Silent Sentry's confidential intake & discovery form to receive a tailored, vendor-neutral technology strategy assessment — no cost, no obligation."
      />
      <Header />
      <main className="pt-16">
        <section className="border-b border-white/10 bg-[#0B1035]">
          <div className="max-w-4xl mx-auto px-6 py-16 md:py-20">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-violet-400 mb-4">Confidential Discovery</p>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium">Customer Intake & Discovery Form</h1>
            <p className="mt-6 text-slate-300 leading-relaxed max-w-2xl">
              Thank you for your interest in Silent Sentry. Please complete this form so we can understand your environment,
              priorities, and objectives. All information is treated as confidential and used solely to prepare a tailored strategy assessment.
            </p>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-6 py-16">
          {submitted ? (
            <div data-testid="intake-success" className="border border-cyan-400/30 bg-[#0B1035] p-10 text-center">
              <ShieldCheck className="w-12 h-12 text-cyan-400 mx-auto" />
              <h2 className="mt-6 font-heading text-3xl font-medium">Submission received</h2>
              <p className="mt-4 text-slate-300 max-w-md mx-auto">
                Our Cybersecurity & Technology Advisory Practice will review your details and contact you via your preferred method to schedule a confidential strategy assessment.
              </p>
              <a href="/" data-testid="intake-back-home" className="mt-8 inline-flex items-center gap-2 bg-cyan-400 text-[#05071F] font-semibold px-7 py-3.5 rounded-full hover:bg-cyan-300 transition-colors">
                Back to Home <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          ) : (
            <form data-testid="intake-form" onSubmit={submit} className="space-y-8">
              <Section num="1" title="Organization Information">
                <Field label="Organization / Agency Name *">
                  <Input data-testid="input-org-name" className={inputCls} value={form.org_name} onChange={set("org_name")} placeholder="Acme County IT Department" />
                </Field>
                <Field label="Primary Industry / Sector">
                  <Input data-testid="input-industry" className={inputCls} value={form.industry} onChange={set("industry")} placeholder="Government, Healthcare, Defense…" />
                </Field>
                <Field label="Organization Type" full>
                  <RadioPills prefix="orgtype" options={ORG_TYPES} value={form.org_type} onChange={(v) => setForm((f) => ({ ...f, org_type: v }))} />
                </Field>
                <Field label="HQ City / State">
                  <Input data-testid="input-hq" className={inputCls} value={form.hq_city_state} onChange={set("hq_city_state")} placeholder="Omaha, NE" />
                </Field>
                <Field label="Number of Employees">
                  <Input data-testid="input-employees" className={inputCls} value={form.num_employees} onChange={set("num_employees")} placeholder="250" />
                </Field>
                <Field label="Primary Website">
                  <Input data-testid="input-website" className={inputCls} value={form.website} onChange={set("website")} placeholder="www.example.gov" />
                </Field>
                <Field label="Annual IT Budget (approx.)">
                  <Input data-testid="input-budget" className={inputCls} value={form.it_budget} onChange={set("it_budget")} placeholder="$500K – $2M" />
                </Field>
              </Section>

              <Section num="2" title="Primary Contact">
                <Field label="Full Name *">
                  <Input data-testid="input-full-name" className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="Jane Anderson" />
                </Field>
                <Field label="Title / Role">
                  <Input data-testid="input-title" className={inputCls} value={form.title_role} onChange={set("title_role")} placeholder="CIO" />
                </Field>
                <Field label="Email Address *">
                  <Input data-testid="input-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jane@example.gov" />
                </Field>
                <Field label="Phone">
                  <Input data-testid="input-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(402) 555-0100" />
                </Field>
                <Field label="Preferred Contact Method" full>
                  <RadioPills prefix="contact" options={CONTACT_METHODS} value={form.preferred_contact} onChange={(v) => setForm((f) => ({ ...f, preferred_contact: v }))} />
                </Field>
              </Section>

              <Section num="3" title="Services of Interest (select all that apply)">
                <div className="sm:col-span-2">
                  <CheckGroup prefix="service" options={SERVICES} values={form.services} onToggle={toggle("services")} />
                </div>
              </Section>

              <Section num="4" title="Current Environment Snapshot">
                <div className="sm:col-span-2">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox data-testid="input-environment-na" checked={form.environment_na}
                      onCheckedChange={(v) => setForm((f) => ({
                        ...f, environment_na: !!v,
                        ...(v ? { cloud_providers: [], security_stack: "", rmm_tools: "", network_summary: "", num_locations: "", num_endpoints: "", compliance: [] } : {}),
                      }))}
                      className="border-white/30 data-[state=checked]:bg-cyan-400 data-[state=checked]:text-[#05071F] mt-0.5" />
                    <span className="text-sm text-slate-300">N/A — not applicable or unsure. Skip this section; we'll cover it on our first call.</span>
                  </label>
                </div>
                <Field label="Primary Cloud Provider(s)" full>
                  <CheckGroup prefix="cloud" options={CLOUDS} values={form.cloud_providers} onToggle={toggle("cloud_providers")} disabled={form.environment_na} />
                </Field>
                <Field label="Current Security Stack (EDR, SIEM, Firewall…)">
                  <Input data-testid="input-security-stack" disabled={form.environment_na} className={inputCls} value={form.security_stack} onChange={set("security_stack")} />
                </Field>
                <Field label="Existing RMM / Management Tools">
                  <Input data-testid="input-rmm" disabled={form.environment_na} className={inputCls} value={form.rmm_tools} onChange={set("rmm_tools")} />
                </Field>
                <Field label="Network Architecture Summary" full>
                  <Textarea data-testid="input-network-summary" disabled={form.environment_na} className={inputCls} rows={3} value={form.network_summary} onChange={set("network_summary")} />
                </Field>
                <Field label="# of Locations / Sites">
                  <Input data-testid="input-locations" disabled={form.environment_na} className={inputCls} value={form.num_locations} onChange={set("num_locations")} />
                </Field>
                <Field label="# of Endpoints (approx.)">
                  <Input data-testid="input-endpoints" disabled={form.environment_na} className={inputCls} value={form.num_endpoints} onChange={set("num_endpoints")} />
                </Field>
                <Field label="Compliance Requirements" full>
                  <CheckGroup prefix="compliance" options={COMPLIANCE} values={form.compliance} onToggle={toggle("compliance")} disabled={form.environment_na} />
                </Field>
              </Section>

              <Section num="5" title="Project Drivers & Timeline">
                <Field label="Primary Business Drivers / Pain Points" full>
                  <Textarea data-testid="input-drivers" className={inputCls} rows={3} value={form.business_drivers} onChange={set("business_drivers")} />
                </Field>
                <Field label="Desired Outcomes / Success Metrics" full>
                  <Textarea data-testid="input-outcomes" className={inputCls} rows={3} value={form.desired_outcomes} onChange={set("desired_outcomes")} />
                </Field>
                <Field label="Target Start Date">
                  <Input data-testid="input-start-date" className={inputCls} value={form.target_start} onChange={set("target_start")} placeholder="Q3 2026" />
                </Field>
                <Field label="Budget Range / Constraints (optional)">
                  <Input data-testid="input-budget-range" className={inputCls} value={form.budget_range} onChange={set("budget_range")} />
                </Field>
                <Field label="Decision Timeline" full>
                  <RadioPills prefix="timeline" options={TIMELINES} value={form.decision_timeline} onChange={(v) => setForm((f) => ({ ...f, decision_timeline: v }))} />
                </Field>
              </Section>

              <Section num="6" title="Additional Context & Notes">
                <Field label="Additional details, constraints, incumbent vendors, known risks, or specific questions:" full>
                  <Textarea data-testid="input-notes" className={inputCls} rows={4} value={form.additional_notes} onChange={set("additional_notes")} />
                </Field>
              </Section>

              <Section num="7" title="Authorization & Next Steps">
                <Field label="Printed Name / Title">
                  <Input data-testid="input-printed-name" className={inputCls} value={form.printed_name} onChange={set("printed_name")} />
                </Field>
                <div className="sm:col-span-2">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox data-testid="input-authorized" checked={form.authorized}
                      onCheckedChange={(v) => setForm((f) => ({ ...f, authorized: !!v }))}
                      className="border-white/30 data-[state=checked]:bg-cyan-400 data-[state=checked]:text-[#05071F] mt-0.5" />
                    <span className="text-sm text-slate-400 leading-relaxed">
                      By submitting this form, I authorize Silent Sentry, LLC to contact me regarding the information provided
                      and to prepare a confidential strategy & technology assessment. No obligation is created by completing this form.
                    </span>
                  </label>
                </div>
              </Section>

              <button type="submit" data-testid="intake-submit-btn" disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-cyan-400 text-[#05071F] font-semibold px-7 py-4 rounded-full hover:bg-cyan-300 transition-colors disabled:opacity-60">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
                {submitting ? "Submitting…" : "Submit Confidential Intake"}
              </button>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
